import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CheckCircle, Building2, MapPin, FileText, Globe, User } from 'lucide-react';
import { businessService } from '../../services/business-service';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

export function BusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    action: 'enable' | 'disable';
  }>({
    isOpen: false,
    action: 'enable',
  });
  const [reason, setReason] = useState('');

  const { data: business, isLoading, error } = useQuery({
    queryKey: ['business', id],
    queryFn: () => businessService.getById(id!),
    enabled: !!id,
  });

  const setStatusMutation = useMutation({
    mutationFn: ({ isEnabled, reason }: { isEnabled: boolean; reason?: string }) =>
      businessService.setStatus(id!, { isEnabled, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business', id] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setConfirmDialog({ isOpen: false, action: 'enable' });
      setReason('');
    },
  });

  const handleStatusChange = (action: 'enable' | 'disable') => {
    setConfirmDialog({ isOpen: true, action });
  };

  const handleConfirmStatusChange = () => {
    const isEnabled = confirmDialog.action === 'enable';
    setStatusMutation.mutate({
      isEnabled,
      reason: isEnabled ? undefined : reason,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-ink-muted">Loading business details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
        Error loading business: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  if (!business) {
    return (
      <div className="bg-white/10 border border-white/30 text-ink-primary px-4 py-3 rounded">
        Business not found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/businesses')}
            className="p-2 text-ink-secondary hover:bg-white/10 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-ink-primary">{business.companyName}</h2>
            <p className="text-ink-secondary mt-1">{business.displayName}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <StatusBadge isEnabled={business.isEnabled} />
          {business.isEnabled ? (
            <button
              onClick={() => handleStatusChange('disable')}
              className="flex items-center gap-2 px-4 py-2 bg-brand-warm text-white rounded-lg hover:bg-brand-warm/90"
            >
              <Ban size={18} />
              Disable Business
            </button>
          ) : (
            <button
              onClick={() => handleStatusChange('enable')}
              className="flex items-center gap-2 px-4 py-2 bg-brand-confirm text-white rounded-lg hover:bg-brand-confirm/90"
            >
              <CheckCircle size={18} />
              Enable Business
            </button>
          )}
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-brand-primary/20 text-brand-primary-light">
            <Building2 size={24} />
          </div>
          <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Business Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Company Name</label>
            <p className="text-ink-primary">{business.companyName}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Display Name</label>
            <p className="text-ink-primary">{business.displayName}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">NIP</label>
            <p className="text-ink-primary">{business.nip || '-'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">User Email</label>
            <p className="text-ink-primary">{business.userEmail || '-'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Created</label>
            <p className="text-ink-primary">
              {new Date(business.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Last Updated</label>
            <p className="text-ink-primary">
              {new Date(business.updatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-brand-confirm/20 text-brand-confirm">
            <MapPin size={24} />
          </div>
          <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Location</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">City</label>
            <p className="text-ink-primary">{business.city || '-'}</p>
          </div>
        </div>
      </div>

      {/* Website */}
      {(business.website || business.websiteDescription) && (
        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-brand-primary-light/20 text-brand-primary-light">
              <Globe size={24} />
            </div>
            <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Website</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {business.website && (
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">URL</label>
                <a
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary-light hover:text-white hover:underline break-all"
                >
                  {business.website}
                </a>
              </div>
            )}
            {business.websiteDescription && (
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">Description</label>
                <p className="text-ink-primary">{business.websiteDescription}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact Person */}
      {(business.contactPersonName || business.contactPersonSurname) && (
        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-brand-primary/20 text-brand-primary-light">
              <User size={24} />
            </div>
            <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Contact Person</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {business.contactPersonName && (
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">First Name</label>
                <p className="text-ink-primary">{business.contactPersonName}</p>
              </div>
            )}
            {business.contactPersonSurname && (
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">Last Name</label>
                <p className="text-ink-primary">{business.contactPersonSurname}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Integration & Features */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-brand-accent/20 text-brand-accent">
            <FileText size={24} />
          </div>
          <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Integrations & Features</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">TPay Integration</label>
            <p className="text-ink-primary">
              {business.hasTPay ? (
                <span className="text-brand-confirm font-medium">Enabled</span>
              ) : (
                <span className="text-ink-muted">Disabled</span>
              )}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">KSeF Integration</label>
            <p className="text-ink-primary">
              {business.hasKSeF ? (
                <span className="text-brand-confirm font-medium">Enabled</span>
              ) : (
                <span className="text-ink-muted">Disabled</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <p className="text-sm text-ink-secondary mb-2">Facilities</p>
          <p className="text-3xl font-bold text-ink-primary">{business.facilityCount}</p>
        </div>

        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <p className="text-sm text-ink-secondary mb-2">Products</p>
          <p className="text-3xl font-bold text-ink-primary">{business.productCount}</p>
        </div>
      </div>

      {/* Disabled Info */}
      {!business.isEnabled && business.disabledAt && (
        <div className="bg-brand-warm/15 border border-brand-warm/40 rounded-lg p-6">
          <h3 className="font-display text-lg font-semibold tracking-tight text-brand-warm mb-4">Disabled Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-warm mb-1">Disabled At</label>
              <p className="text-ink-primary">
                {new Date(business.disabledAt).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
            {business.disabledReason && (
              <div>
                <label className="block text-sm font-medium text-brand-warm mb-1">Reason</label>
                <p className="text-ink-primary">{business.disabledReason}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => {
          setConfirmDialog({ isOpen: false, action: 'enable' });
          setReason('');
        }}
        onConfirm={handleConfirmStatusChange}
        title={confirmDialog.action === 'enable' ? 'Enable Business' : 'Disable Business'}
        message={`Are you sure you want to ${confirmDialog.action} "${business.companyName}"?`}
        confirmText={confirmDialog.action === 'enable' ? 'Enable' : 'Disable'}
        confirmVariant={confirmDialog.action === 'disable' ? 'danger' : 'primary'}
        isLoading={setStatusMutation.isPending}
      >
        {confirmDialog.action === 'disable' && (
          <div className="mt-4">
            <label htmlFor="reason" className="block text-sm font-medium text-white/70 mb-2">
              Reason for disabling (optional)
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              rows={3}
              placeholder="Enter reason..."
            />
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}
